import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import definite from 'webapp/helpers/definite';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type { Board } from 'webapp/models-custom';
import BoardLoop from 'webapp/models-custom/board-loop';

interface Args {
	selected: number[];
	allItems: Board[];
	onAddSelected: (items: number[]) => void;
	onRemoveSelected: (item: number) => void;
}

export default class SortableBoardListComponent extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	@tracked itemsSelected: Board[] = []; // shown on list (not necessarily in the right order)

	el!: JQuery;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updateItemsSelected();
	}

	get itemsNotSelected(): Board[] {
		return this.args.allItems.filter((board) => {
			return !this.args.selected.includes(board.numericId!);
		});
	}

	get showAddLink(): boolean {
		return this.itemsNotSelected.length > 0;
	}

	getOrderedItemIds(): number[] {
		const ids: number[] = [];

		this.el.find('li').each((_index: number, element: HTMLElement) => {
			const id = $(element).attr('data-id');
			if (id) {
				ids.push(+id);
			}
		});

		return ids;
	}

	@action
	updateItemsSelected(): void {
		const allItems = this.args.allItems,
			itemsSelected: Board[] = [];

		this.args.selected.forEach((id) => {
			const item = allItems.find((item) => item.numericId === id);
			if (item) {
				itemsSelected.push(item);
			}
		});

		this.itemsSelected = itemsSelected;
	}

	@action
	initSortable(node: HTMLElement): void {
		if (node) {
			this.el = $(node).find('ul');

			this.el.sortable({
				items: 'li',
				handle: '.handle',
				axis: 'y',
				containment: 'parent',
				tolerance: 'pointer', // otherwise containment parent cannot handle 1st or last sorts
				update: () => {
					const ids = this.getOrderedItemIds();
					this.args.onAddSelected(ids);
				},
			});
		}
	}

	@action
	destroySortable(): void {
		const el = this.el;
		if (el.length && el.sortable('instance')) {
			el.sortable('destroy');
		}
	}

	@action
	addItem(): void {
		const popup = this.mmBoardsService.createBoardsSelectListModal();

		popup.open({
			model: this.itemsNotSelected,
			maxSelectable: BoardLoop.maxBoardsInLoop - this.args.selected.length,
			onConfirm: (selected: number[]) => {
				if (isAlive(this)) {
					const newSelected = [...this.args.selected];
					selected.forEach((id) => {
						if (!newSelected.includes(id)) {
							newSelected.push(id);
						}
					});
					this.args.onAddSelected(newSelected);
				}
				return Promise.resolve(true);
			},
		});
	}

	@action
	deleteItem(value: number): void {
		this.args.onRemoveSelected(value);
	}
	<template>
		<div
			class="sortable-list"
			{{didInsert this.initSortable}}
			{{didUpdate this.updateItemsSelected @selected}}
			{{willDestroy this.destroySortable}}
		>
			<ul class={{unless this.itemsSelected "empty"}}>
				{{#each this.itemsSelected as |board|}}
					<li data-id={{board.id}}>
						<CommonUiIconComponent @name="List" @size="xs" @class="handle" />
						<span class="text">{{board.name}}</span>
						<a
							role="button"
							class="remove"
							{{on "click" (fn this.deleteItem (definite board.numericId))}}
						><CommonUiIconComponent @name="Close" @size="xs" />
						</a>
					</li>
				{{/each}}
			</ul>

			{{#if this.showAddLink}}
				<a class="add" {{on "click" this.addItem}}>+ Add dashboards</a>
			{{/if}}
		</div>
	</template>
}
